import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkScheduling = ({ onBulkSchedule, onClose }) => {
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [bulkSettings, setBulkSettings] = useState({
    interviewerId: '',
    type: 'video',
    duration: 60,
    startDate: '',
    endDate: '',
    timeSlots: [],
    location: '',
    meetingLink: '',
    notes: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock data
  const candidates = [
    { id: 'c1', name: 'Sarah Johnson', position: 'Senior Frontend Developer', score: 92 },
    { id: 'c2', name: 'Michael Chen', position: 'Full Stack Engineer', score: 88 },
    { id: 'c3', name: 'Emily Rodriguez', position: 'UX/UI Designer', score: 85 },
    { id: 'c4', name: 'David Thompson', position: 'Backend Developer', score: 90 },
    { id: 'c5', name: 'Lisa Wang', position: 'Product Manager', score: 87 },
    { id: 'c6', name: 'James Wilson', position: 'DevOps Engineer', score: 83 },
    { id: 'c7', name: 'Anna Martinez', position: 'Data Scientist', score: 91 },
    { id: 'c8', name: 'Kevin Brown', position: 'Mobile Developer', score: 86 }
  ];

  const interviewers = [
    { value: 'i1', label: 'John Smith', description: 'Senior Technical Lead' },
    { value: 'i2', label: 'Maria Garcia', description: 'HR Manager' },
    { value: 'i3', label: 'Robert Wilson', description: 'Engineering Manager' },
    { value: 'i4', label: 'Jennifer Lee', description: 'Product Director' }
  ];

  const interviewTypes = [
    { value: 'phone', label: 'Phone Interview' },
    { value: 'video', label: 'Video Interview' },
    { value: 'in-person', label: 'In-Person Interview' }
  ];

  const durations = [
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' }
  ];

  const timeSlotOptions = [
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' }
  ];

  const handleCandidateToggle = (candidateId) => {
    setSelectedCandidates(prev => 
      prev?.includes(candidateId)
        ? prev?.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCandidates?.length === candidates?.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(candidates?.map(c => c?.id));
    }
  };

  const handleTimeSlotToggle = (timeSlot) => {
    setBulkSettings(prev => ({
      ...prev,
      timeSlots: prev?.timeSlots?.includes(timeSlot)
        ? prev?.timeSlots?.filter(slot => slot !== timeSlot)
        : [...prev?.timeSlots, timeSlot]
    }));
  };

  const handleSettingChange = (field, value) => {
    setBulkSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateSchedule = () => {
    const schedule = [];
    const startDate = new Date(bulkSettings.startDate);
    const endDate = new Date(bulkSettings.endDate);
    
    let currentDate = new Date(startDate);
    let candidateIndex = 0;

    while (currentDate <= endDate && candidateIndex < selectedCandidates?.length) {
      // Skip weekends
      if (currentDate?.getDay() !== 0 && currentDate?.getDay() !== 6) {
        for (const timeSlot of bulkSettings?.timeSlots) {
          if (candidateIndex >= selectedCandidates?.length) break;
          
          const candidate = candidates?.find(c => c?.id === selectedCandidates?.[candidateIndex]);
          const dateTime = new Date(currentDate);
          const [hours, minutes] = timeSlot?.split(':');
          dateTime?.setHours(parseInt(hours), parseInt(minutes));

          schedule?.push({
            candidateId: candidate?.id,
            candidateName: candidate?.name,
            dateTime: dateTime?.toISOString(),
            ...bulkSettings
          });

          candidateIndex++;
        }
      }
      currentDate?.setDate(currentDate?.getDate() + 1);
    }

    return schedule;
  };

  const handleBulkSchedule = async () => {
    setIsProcessing(true);
    
    try {
      const schedule = generateSchedule();
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      onBulkSchedule(schedule);
    } catch (error) {
      console.error('Error creating bulk schedule:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const isFormValid = () => {
    return selectedCandidates?.length > 0 &&
           bulkSettings?.interviewerId &&
           bulkSettings?.startDate &&
           bulkSettings?.endDate &&
           bulkSettings?.timeSlots?.length > 0 &&
           (bulkSettings?.type !== 'in-person' || bulkSettings?.location) &&
           (bulkSettings?.type !== 'video' || bulkSettings?.meetingLink);
  };

  const previewSchedule = generateSchedule();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={18} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Bulk Interview Scheduling</h2>
              <p className="text-sm text-muted-foreground">Schedule multiple interviews efficiently</p>
            </div>
          </div>
          <Button variant="outline" size="sm" iconName="X" onClick={onClose} />
        </div>

        <div className="flex h-[calc(90vh-80px)]">
          {/* Left Panel - Candidate Selection */}
          <div className="w-1/2 border-r border-border p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">Select Candidates</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedCandidates?.length === candidates?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <div className="space-y-3">
              {candidates?.map(candidate => (
                <div
                  key={candidate?.id}
                  className={`p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                    selectedCandidates?.includes(candidate?.id)
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleCandidateToggle(candidate?.id)}
                >
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={selectedCandidates?.includes(candidate?.id)}
                      onChange={() => handleCandidateToggle(candidate?.id)}
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{candidate?.name}</h4>
                      <p className="text-sm text-muted-foreground">{candidate?.position}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-foreground">Score: {candidate?.score}%</div>
                      <div className="w-16 h-2 bg-muted rounded-full mt-1">
                        <div 
                          className="h-full bg-success rounded-full"
                          style={{ width: `${candidate?.score}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Panel - Bulk Settings */}
          <div className="w-1/2 p-6 overflow-y-auto">
            <h3 className="text-lg font-medium text-foreground mb-4">Bulk Settings</h3>

            <div className="space-y-4">
              {/* Interviewer */}
              <Select
                label="Assign Interviewer"
                required
                options={interviewers}
                value={bulkSettings?.interviewerId}
                onChange={(value) => handleSettingChange('interviewerId', value)}
              />

              {/* Interview Type and Duration */}
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Interview Type"
                  required
                  options={interviewTypes}
                  value={bulkSettings?.type}
                  onChange={(value) => handleSettingChange('type', value)}
                />
                <Select
                  label="Duration"
                  required
                  options={durations}
                  value={bulkSettings?.duration}
                  onChange={(value) => handleSettingChange('duration', value)}
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Start Date"
                  type="date"
                  required
                  value={bulkSettings?.startDate}
                  onChange={(e) => handleSettingChange('startDate', e?.target?.value)}
                />
                <Input
                  label="End Date"
                  type="date"
                  required
                  value={bulkSettings?.endDate}
                  onChange={(e) => handleSettingChange('endDate', e?.target?.value)}
                />
              </div>

              {/* Time Slots */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Available Time Slots *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {timeSlotOptions?.map(slot => (
                    <label
                      key={slot?.value}
                      className={`flex items-center space-x-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        bulkSettings?.timeSlots?.includes(slot?.value)
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <Checkbox
                        checked={bulkSettings?.timeSlots?.includes(slot?.value)}
                        onChange={() => handleTimeSlotToggle(slot?.value)}
                      />
                      <span className="text-sm text-foreground">{slot?.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Conditional Fields */}
              {bulkSettings?.type === 'in-person' && (
                <Input
                  label="Location"
                  type="text"
                  placeholder="Conference Room A, Building 1"
                  required
                  value={bulkSettings?.location}
                  onChange={(e) => handleSettingChange('location', e?.target?.value)}
                />
              )}

              {bulkSettings?.type === 'video' && (
                <Input
                  label="Meeting Link Template"
                  type="url"
                  placeholder="https://zoom.us/j/meeting-room"
                  required
                  value={bulkSettings?.meetingLink}
                  onChange={(e) => handleSettingChange('meetingLink', e?.target?.value)}
                />
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Default Notes
                </label>
                <textarea
                  rows={3}
                  placeholder="Add default notes for all interviews..."
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  value={bulkSettings?.notes}
                  onChange={(e) => handleSettingChange('notes', e?.target?.value)}
                />
              </div>

              {/* Preview */}
              {previewSchedule?.length > 0 && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-foreground mb-2">
                    Schedule Preview ({previewSchedule?.length} interviews)
                  </h4>
                  <div className="text-xs text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                    {previewSchedule?.slice(0, 5)?.map((item, index) => (
                      <div key={index}>
                        {item?.candidateName} - {new Date(item.dateTime)?.toLocaleDateString()} at {new Date(item.dateTime)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    ))}
                    {previewSchedule?.length > 5 && (
                      <div className="text-accent">...and {previewSchedule?.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border mt-6">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkSchedule}
                loading={isProcessing}
                disabled={!isFormValid()}
                iconName="Calendar"
                iconPosition="left"
              >
                Schedule {selectedCandidates?.length} Interview{selectedCandidates?.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkScheduling;